import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Check, CreditCard, DollarSign, AlertCircle, UserPlus, X, Search, Clock } from 'lucide-react';
import { fmtMoney } from '../utils/money';
import { SolicitudesCreditoPunto } from '../components/SolicitudesCreditoPunto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AbonosClientes() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clienteId, setClienteId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [monto, setMonto] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  // Las vendedoras no registran abonos: los solicitan y administración los autoriza
  const esAdmin = user?.role === 'admin' || user?.role === 'propietario';

  const [showNuevoCliente, setShowNuevoCliente] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTelefono, setNuevoTelefono] = useState('');
  const [creandoCliente, setCreandoCliente] = useState(false);

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const { data } = await axios.get(`${API}/clientes/${user.punto_venta_id}`);
      setClientes(data.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
    } catch (err) {
      setError('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const clienteSeleccionado = clientes.find(c => c.id === clienteId);
  const deuda = clienteSeleccionado ? Math.abs(Math.min(clienteSeleccionado.saldo, 0)) : 0;
  const clientesFiltrados = busqueda.trim()
    ? clientes.filter(c => c.nombre.toLowerCase().includes(busqueda.toLowerCase()))
    : clientes;

  const handleCrearCliente = async () => {
    if (!nuevoNombre.trim()) return;
    setCreandoCliente(true);
    try {
      const { data } = await axios.post(`${API}/clientes`, {
        nombre: nuevoNombre.trim(),
        telefono: nuevoTelefono.trim() || null,
      });
      setClientes(prev => [...prev, data]);
      setClienteId(data.id);
      setNuevoNombre('');
      setNuevoTelefono('');
      setShowNuevoCliente(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear cliente');
    } finally {
      setCreandoCliente(false);
    }
  };

  const handleSubmit = async () => {
    if (!clienteId) {
      setError('Selecciona un cliente');
      return;
    }
    const montoNum = parseFloat(monto);
    if (!montoNum || montoNum <= 0) {
      setError('Ingresa un monto válido');
      return;
    }

    setError('');
    setSubmitting(true);
    if (!esAdmin) {
      try {
        await axios.post(`${API}/solicitudes-credito`, { tipo: 'abono', cliente_id: clienteId, monto: montoNum });
        setSuccess({ nombre: clienteSeleccionado?.nombre, monto: montoNum, solicitud: true });
      } catch (err) {
        setError(err.response?.data?.detail || 'Error al enviar la solicitud');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    try {
      const { data } = await axios.post(`${API}/abonos`, {
        cliente_id: clienteId,
        monto: montoNum,
      });
      setSuccess({
        nombre: clienteSeleccionado?.nombre,
        monto: montoNum,
        nuevo_saldo: data.nuevo_saldo,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar abono');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (success?.solicitud) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
          <Clock className="w-10 h-10 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900 mb-1">Solicitud de abono enviada</h1>
        <p className="text-zinc-500 mb-4">{success.nombre}</p>
        <p className="text-4xl font-bold text-blue-600 mb-2">{fmtMoney(success.monto)}</p>
        <p className="text-sm text-zinc-500 max-w-xs">
          Administración debe autorizarla. Guarda el dinero: si la rechazan, aparecerá como sobrante en tu corte
          y deberás entregarlo. No podrás cerrar tu turno mientras esté pendiente.
        </p>
        <div className="mt-8 flex gap-3">
          <button
            onClick={() => { setSuccess(null); setMonto(''); setClienteId(''); loadClientes(); }}
            className="px-6 py-3 border border-zinc-300 rounded-xl font-medium text-zinc-700"
          >
            Otra solicitud
          </button>
          <button onClick={() => navigate('/pos')} className="px-6 py-3 bg-zinc-900 text-white rounded-xl font-medium">
            Volver al POS
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    const saldoRestante = Math.abs(Math.min(success.nuevo_saldo, 0));
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900 mb-1">Abono registrado</h1>
        <p className="text-zinc-500 mb-4">{success.nombre}</p>
        <p className="text-4xl font-bold text-green-600 mb-2">{fmtMoney(success.monto)}</p>
        {saldoRestante > 0 ? (
          <p className="text-sm text-orange-600">Saldo pendiente: {fmtMoney(saldoRestante)}</p>
        ) : (
          <p className="text-sm text-green-600">Cuenta al corriente</p>
        )}
        <div className="mt-8 flex gap-3">
          <button
            onClick={() => { setSuccess(null); setMonto(''); setClienteId(''); loadClientes(); }}
            className="px-6 py-3 border border-zinc-300 rounded-xl font-medium text-zinc-700"
          >
            Otro abono
          </button>
          <button
            onClick={() => navigate('/pos')}
            className="px-6 py-3 bg-zinc-900 text-white rounded-xl font-medium"
          >
            Volver al POS
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pos')}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Abonos de Clientes
            </h1>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Client selector */}
        <div>
          <p className="text-xs font-medium text-zinc-500 tracking-wide mb-2">CLIENTE</p>

          {clienteSeleccionado ? (
            <div className="flex items-center justify-between px-4 py-3 border border-zinc-300 rounded-xl bg-zinc-50">
              <div>
                <p className="font-medium text-zinc-900">{clienteSeleccionado.nombre}</p>
                <p className={`text-sm ${clienteSeleccionado.saldo < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {clienteSeleccionado.saldo < 0 ? `Debe ${fmtMoney(Math.abs(clienteSeleccionado.saldo))}` : 'Al corriente'}
                </p>
              </div>
              <button onClick={() => { setClienteId(''); setBusqueda(''); }} className="text-zinc-400 hover:text-zinc-600">
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  placeholder="Buscar cliente por nombre..."
                  className="w-full h-11 pl-9 pr-4 border border-zinc-300 rounded-xl text-sm text-zinc-900"
                  autoComplete="off"
                />
              </div>
              {busqueda.trim() && (
                <div className="border border-zinc-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                  {clientesFiltrados.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-zinc-400">Sin resultados</p>
                  ) : (
                    clientesFiltrados.map(c => (
                      <button
                        key={c.id}
                        onClick={() => { setClienteId(c.id); setBusqueda(''); }}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 border-b border-zinc-100 last:border-0 text-left"
                      >
                        <span className="font-medium text-zinc-900">{c.nombre}</span>
                        <span className={`text-sm ${c.saldo < 0 ? 'text-red-500' : 'text-green-600'}`}>
                          {c.saldo < 0 ? `debe ${fmtMoney(Math.abs(c.saldo))}` : 'al corriente'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {!showNuevoCliente ? (
            <button
              onClick={() => setShowNuevoCliente(true)}
              className="mt-2 flex items-center gap-2 text-blue-600 text-sm font-medium"
            >
              <UserPlus className="w-4 h-4" />
              Nuevo cliente
            </button>
          ) : (
            <div className="mt-3 bg-blue-50 rounded-xl p-4 space-y-3 border border-blue-200">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-blue-700">Nuevo cliente</p>
                <button onClick={() => setShowNuevoCliente(false)}><X className="w-4 h-4 text-blue-400" /></button>
              </div>
              <input
                type="text"
                value={nuevoNombre}
                onChange={e => setNuevoNombre(e.target.value)}
                placeholder="Nombre del cliente"
                className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm"
              />
              <input
                type="tel"
                value={nuevoTelefono}
                onChange={e => setNuevoTelefono(e.target.value)}
                placeholder="Teléfono (opcional)"
                className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm"
              />
              <button
                onClick={handleCrearCliente}
                disabled={!nuevoNombre.trim() || creandoCliente}
                className="w-full h-11 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {creandoCliente ? 'Guardando...' : 'Guardar cliente'}
              </button>
            </div>
          )}
        </div>

        {/* Client balance info */}
        {clienteSeleccionado && (
          <div className={`rounded-xl p-4 ${deuda > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
            <p className="font-bold text-zinc-900">{clienteSeleccionado.nombre}</p>
            {clienteSeleccionado.telefono && (
              <p className="text-sm text-zinc-500">{clienteSeleccionado.telefono}</p>
            )}
            <p className={`text-lg font-bold mt-1 ${deuda > 0 ? 'text-orange-600' : 'text-green-600'}`}>
              {deuda > 0 ? `Debe: ${fmtMoney(deuda)}` : 'Sin adeudo'}
            </p>
          </div>
        )}

        {/* Amount input */}
        <div>
          <label className="block text-xs font-medium text-zinc-500 tracking-wide mb-2">
            <DollarSign className="w-4 h-4 inline mr-1" />
            MONTO DEL ABONO
          </label>
          <input
            type="number"
            value={monto}
            onChange={e => setMonto(e.target.value)}
            placeholder="0.00"
            className="w-full h-16 px-4 bg-white border border-zinc-300 rounded-xl text-3xl font-bold text-center"
            inputMode="decimal"
          />
          {deuda > 0 && monto && (
            <div className="mt-2 p-2 bg-zinc-50 rounded-lg text-sm text-center">
              Saldo restante: {fmtMoney(Math.max(0, deuda - (parseFloat(monto) || 0)))}
            </div>
          )}
        </div>

        {!esAdmin && (
          <div className="pt-2">
            <SolicitudesCreditoPunto puntoVentaId={user?.punto_venta_id} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 pb-8 pt-4 border-t border-zinc-200">
        <button
          onClick={handleSubmit}
          disabled={submitting || !clienteId || !monto}
          className="w-full h-14 bg-blue-600 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting ? (
            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-5 h-5" />
              {esAdmin ? 'Registrar Abono' : 'Solicitar abono a administración'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
